import { IDevice } from '@pages/Assets/Inventory/types';
import { Alert, Radio, Select } from 'antd';
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import useSwr from '@hooks/useSwr';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import ActionTypes from '@utils/deviceActions';
import { MinusCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { IDeviceActionRequestPayload } from '../types';
import * as S from './SuspensionWithdraw/styles';
import useUpload from './Upload';

interface INicknameChangeProps {
  currentStep: number;
  devicesProps: IDevice[] | undefined;
  handleDeviceActions: (item: IDeviceActionRequestPayload) => void;
  uploadedDevices: any[];
  setUploadedDevices: Dispatch<SetStateAction<any[]>>;
}

function useChangeApn({
  currentStep,
  devicesProps,
  handleDeviceActions,
  uploadedDevices,
  setUploadedDevices,
}: INicknameChangeProps) {
  const [apnSelected, setApnSelected] = useState();
  const { t } = useTranslation('device-actions-modal');
  const { keycloak } = useContext(KeycloakContext);

  const [action, setAction] = useState<
    ActionTypes.APN_ADD | ActionTypes.APN_REMOVE
  >();

  const { data: apnsList } = useSwr<string[]>(
    `/service-proxy/apns-customer/${keycloak?.idTokenParsed?.customerId}`,
  );

  const { uploadStep, uploadReinitState } = useUpload({
    actionKey: action ?? ActionTypes.APN_ADD,
    setUploadedDevices,
    customInfo: action === ActionTypes.APN_REMOVE && (
      <Alert
        showIcon
        description={t('steps.change-apn.remove-all-apns-help')}
      />
    ),
  });

  const apnOptions = useMemo(() => {
    const allApns = apnsList?.map(apn => ({
      label: apn,
      value: apn,
    }));

    if (action === ActionTypes.APN_ADD) {
      return allApns;
    }

    return [
      {
        label: t('steps.change-apn.allApns'),
        value: 'ALL',
      },
      {
        label: t('steps.change-apn.apnsList'),
        options: allApns,
      },
    ];
  }, [action, apnsList, t]);

  const contentSteps = useMemo(() => {
    const defaultSteps = [
      {
        title: t('steps.change-apn.action'),
        content: (
          <S.Container>
            <Radio.Group
              className="action-lock-unlock"
              value={action}
              onChange={({ target: { value } }) => {
                setAction(value);
              }}
            >
              <Radio.Button
                value={ActionTypes.APN_ADD}
                className="action-lock-unlock-radio"
              >
                <PlusCircleOutlined />
                {t('steps.change-apn.add')}
              </Radio.Button>
              <Radio.Button
                value={ActionTypes.APN_REMOVE}
                className="action-lock-unlock-radio"
              >
                <MinusCircleOutlined />
                {t('steps.change-apn.remove')}
              </Radio.Button>
            </Radio.Group>
          </S.Container>
        ),
      },
    ];

    if (devicesProps && devicesProps?.length > 0) {
      defaultSteps.push({
        title: t('steps.change-apn.select-apn'),
        content: (
          <Select
            showSearch
            value={apnSelected}
            onChange={event => setApnSelected(event)}
            style={{ width: '100%', marginTop: 8 }}
            optionFilterProp="label"
            options={apnOptions}
          />
        ),
      });
    } else {
      defaultSteps.push(uploadStep);
    }

    return defaultSteps;
  }, [action, apnOptions, apnSelected, devicesProps, t, uploadStep]);

  const reinitState = () => {
    setApnSelected(undefined);
    setAction(undefined);
    setUploadedDevices([]);
    uploadReinitState();
  };

  const nextStepIsDisabled = useCallback(() => {
    const isUpload = !devicesProps || devicesProps?.length === 0;

    const actionNotSelected = currentStep === 0 && !action;

    const apnNotSelected =
      currentStep === 1 &&
      (isUpload ? uploadedDevices?.length === 0 : !apnSelected);

    return actionNotSelected || apnNotSelected;
  }, [action, apnSelected, currentStep, devicesProps, uploadedDevices]);

  const onSubmit = () => {
    const payload: IDeviceActionRequestPayload = {};
    payload.action = action;
    payload.devices =
      devicesProps && devicesProps?.length > 0
        ? devicesProps?.map(({ msisdn, iccid }: IDevice) => {
            return { msisdn, iccid, processData: apnSelected };
          })
        : uploadedDevices.map(({ apn, ...device }: IDevice) => ({
            ...device,
            processData: apn,
          }));
    return handleDeviceActions(payload);
  };

  const changeApnData = {
    contentSteps,
    onSubmit,
    reinitState,
    nextStepIsDisabled,
  };

  return { changeApnData };
}
export default useChangeApn;
